import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { toAgentApiError, toAgentErrorMessage } from 'features/yieldbirdWrapperAgent/api/errors'
import { reportsQueryKey } from 'features/yieldbirdWrapperAgent/api/queries'
import { createReport } from 'features/yieldbirdWrapperAgent/api/requests'
import { enqueueSnackbarForRestError } from 'providers/tanstack-query-rest/errors'

import type { Schema } from './schema'
import type { GenerateReportFormProps } from './types'
import type { AgentApiError } from 'features/yieldbirdWrapperAgent/api/errors'
import type { FieldErrors } from 'react-hook-form'

type UseCreateReport = {
  create: (data: Schema) => void
  errors: FieldErrors<Schema>
  isPending: boolean
}

const VALIDATION_FAILED: string = 'VALIDATION_FAILED'

export const useCreateReport = ({
  onCreated,
}: Pick<GenerateReportFormProps, 'onCreated'>): UseCreateReport => {
  const queryClient = useQueryClient()
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  const { isPending, mutate } = useMutation({
    mutationFn: createReport,
    onError: (error: Error): void => {
      const apiError: AgentApiError | undefined = toAgentApiError(error)

      if (apiError?.code === VALIDATION_FAILED) {
        setErrors({ url: { message: apiError.message, type: apiError.code } })

        return
      }

      enqueueSnackbarForRestError(error, toAgentErrorMessage)
    },
    onSuccess: ({ jobId }, { url }): void => {
      setErrors({})
      onCreated({ jobId, url })
      void queryClient.invalidateQueries({ queryKey: reportsQueryKey })
    },
  })

  const create = ({ url }: Schema): void => mutate({ url })

  return { create, errors, isPending }
}
